import type { Rng } from '../core/rng';
import type { ParamSchema, ParamValues } from '../core/params/types';
import type { TextField } from '../core/text/textField';

export type TechniqueCategory =
  | 'field'
  | 'growth'
  | 'packing'
  | 'noise'
  | 'automata'
  | 'physics'
  | 'deconstruction'
  | 'dimension';

export const CATEGORY_LABELS: Record<TechniqueCategory, string> = {
  field: 'Feld & Partikel',
  growth: 'Organisches Wachstum',
  packing: 'Packung',
  noise: 'Rauschverzerrung',
  automata: 'Automaten',
  physics: 'Physik',
  deconstruction: 'Dekonstruktion',
  dimension: 'Dimension',
};

export type Ctx2D = CanvasRenderingContext2D | OffscreenCanvasRenderingContext2D;

export interface RenderTarget {
  ctx: Ctx2D;
  width: number;
  height: number;
  /** Output scale relative to the document size; 1 in the preview, higher on export. */
  scale: number;
}

/**
 * Everything a technique is allowed to know.
 *
 * Note what is *not* here: fonts, layout, other layers, the DOM. A technique sees a
 * field, a size, a random stream and its own parameter values. That is what makes
 * techniques independently testable and freely stackable.
 */
export interface TechniqueContext {
  field: TextField;
  width: number;
  height: number;
  /** Seeded from the layer, so a layer's randomness is stable and independent. */
  rng: Rng;
  values: ParamValues;
  /** Render scale, for techniques that size elements in absolute pixels. */
  scale: number;
  /**
   * Budget hint. The preview lowers this when the frame rate drops; exports always
   * pass 1. Techniques should scale element counts by it, never quality.
   */
  quality: number;
}

/**
 * A technique plugin.
 *
 * Adding one means creating `src/techniques/<id>/` with `params.ts`, `render.ts` and
 * `definition.ts`, then registering it — no other file needs to change. The UI, the
 * randomiser and the batch engine all derive their behaviour from `params`.
 */
export interface Technique<State = unknown> {
  id: string;
  name: string;
  category: TechniqueCategory;
  /** One or two sentences shown when picking the technique. */
  description: string;
  params: ParamSchema;
  renderer: 'canvas2d' | 'webgl';
  /**
   * True when the technique produces continuous motion rather than converging on a
   * final image. Read by the animation exporter; the render loop uses `isComplete`.
   */
  isAnimated: boolean;

  init(ctx: TechniqueContext): State;
  /**
   * Advances the simulation by one frame's worth of work. `dt` is in seconds.
   *
   * Techniques that need many iterations must spread them across frames rather than
   * doing everything in `init` — a flow field drawing 600 000 segments would otherwise
   * freeze the UI on every parameter change. Stepping progressively also means the
   * user watches the image build, which is far better feedback than a stall.
   */
  step(state: State, dt: number, ctx: TechniqueContext): State;
  /**
   * True once further steps would not change the image.
   *
   * The render loop stops stepping and redrawing when every layer reports completion,
   * so a finished composition costs nothing. Endlessly animated techniques always
   * return false; exporters then run a bounded number of steps instead.
   */
  isComplete(state: State): boolean;
  draw(state: State, target: RenderTarget, ctx: TechniqueContext): void;
  /** Optional vector output, used by the SVG and PDF exporters. */
  toSVG?(state: State, ctx: TechniqueContext): string;
  /** Element count for the status bar — particles, circles, branches. */
  elementCount?(state: State): number;
}

/** Convenience alias for techniques whose state type is not exposed. */
export type AnyTechnique = Technique<never>;
